import { auth } from "../firebase";

// ── API base URL ───────────────────────────────────────────────────────────────
// Always use relative paths — /api/* is handled by:
//   • Firebase Hosting: rewrites in firebase.json → Firebase Functions (asia-south1)
//   • Vercel:           serverless functions in /api/ directory
// This avoids the previous bug where cursivo.outreachos.in was hardcoded,
// causing Firebase-hosted pages to call themselves and get index.html back
// (res.json() on HTML → "Server error 200 — please try again.").
export const API_BASE = "";

// ── Auth token helper ──────────────────────────────────────────────────────────
async function getIdToken() {
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in.");
  try {
    return await user.getIdToken();
  } catch (e) {
    // Firebase Auth's internal token refresh can throw its own low-level
    // errors (e.g. from its popup/redirect resolver) that read as generic
    // "Cannot read properties of undefined" with no indication they came
    // from auth at all. Label it so the real source is obvious in logs
    // instead of looking like a bug in whatever called getIdToken().
    throw new Error(`Auth token refresh failed: ${e.message}`);
  }
}

// ── PDF export URL strategy ───────────────────────────────────────────────────
//
// Level 1a — Firebase Cloud Function (asia-south1):
//   Self-hosted Puppeteer, same region as Firestore, binary-safe direct call.
//   Called first on every export.
//
// Level 1b — same-origin retry via Firebase Hosting's own rewrite:
//   `/api/exportPdf` isn't a separate backend — firebase.json rewrites it to
//   this exact same `exportPdf` Cloud Function, just reached same-origin
//   through Hosting instead of the direct asia-south1 Cloud Functions URL.
//   Useful as a fallback specifically because it's same-origin (no CORS
//   preflight, no chance of a cross-origin network quirk failing the direct
//   URL while the app's own origin is fine). There is no Vercel/Browserless
//   deployment in this project — an earlier version of this comment
//   referenced one that was already retired; api/exportPdf.js and
//   BROWSERLESS_TOKEN do not exist in this codebase.
//
// Level 2 — print-iframe / html2canvas fallback:
//   Handled upstream in useCvExport.js. exportPdfBinary() throws on all hard
//   failures so the caller falls through to this level automatically.
//
const PDF_PRIMARY_URL =
  "https://asia-south1-cv-editor-pro.cloudfunctions.net/exportPdf";

// Relative path — works on both Vercel (/api/* serverless) and Firebase Hosting
// (/api/* rewritten to Functions). Only used when the direct Function URL fails.
const PDF_FALLBACK_URL = "/api/exportPdf";

const PRIMARY_TIMEOUT_MS  = 55_000; // 55s — gives Cloud Function its full 60s minus network
const FALLBACK_TIMEOUT_MS = 60_000;

async function _fetchPdf(url, body, timeoutMs, token) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, {
      method:  "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body:    JSON.stringify(body),
      signal:  controller.signal,
    });
    clearTimeout(timer);
    return res;
  } catch (e) {
    clearTimeout(timer);
    throw e; // AbortError or network error — let exportPdfBinary() decide
  }
}

// Generous enough for the slowest AI operations (full_rewrite, jd_tailor) while
// still guaranteeing the caller's loading state eventually clears on a hung request.
const API_POST_TIMEOUT_MS = 60_000;

export async function apiPost(endpoint, body = {}) {
  const token = await getIdToken();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), API_POST_TIMEOUT_MS);
  let res;
  try {
    res = await fetch(`${API_BASE}/api/${endpoint}`, {
      method:  "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify(body),
      signal:  controller.signal,
    });
  } catch (e) {
    throw new Error(
      e.name === "AbortError"
        ? "Request timed out — please try again."
        : "Network error — check your connection and try again."
    );
  } finally {
    clearTimeout(timer);
  }
  let data;
  try { data = await res.json(); } catch { throw new Error(`Server error ${res.status} — please try again.`); }
  if (!res.ok) throw Object.assign(new Error(data.error || `API error ${res.status}`), { code: data.code });
  return data;
}

export async function callAISecure({ operation, systemPrompt, userContent, cv, json=false, temperature=0.3, diff=false, originalBullet="", providerOverride=null, pdfBase64=null }) {
  return apiPost("callAI", { operation, systemPrompt, userContent, cv, json, temperature, diff, originalBullet, providerOverride, pdfBase64 });
}

export async function getAIUsage() {
  return apiPost("getUsage");
}

// Drop-in shim — replaces the old client-side callAI everywhere.
// Keeps the same signature so all existing calls work without changes.
export async function callAI(systemPrompt, userContent, json=false, _aiCfg={}, temperature=0.3) {
  const data = await callAISecure({ systemPrompt, userContent, json, temperature });
  return data.result;
}

const CV_CHAT_SYSTEM = `You are Cursivo's friendly CV-building assistant, guiding a user through describing their work experience, education, and skills via conversation. Ask one focused, specific question at a time. Keep responses concise (2-4 sentences) and encouraging. Do not repeat questions already answered earlier in the transcript.`;

const CV_CHAT_FINALIZE_SYSTEM = `You are an expert resume writer. Read the full conversation transcript and produce a single valid JSON object representing the person's CV, using only information the user actually shared. Your ONLY output is a single valid JSON object — no markdown, no backticks, no explanation, no preamble. Start your response with { and end with }.

JSON structure (use exactly these keys; empty arrays for sections with no information):
{
  "personalInfo": {"name":"","email":"","phone":"","location":"","linkedin":"","website":"","summary":""},
  "experience": [{"company":"","role":"","period":"","duration":"","bullets":[{"text":"","hasMetric":false}]}],
  "positions": [{"role":"","org":"","department":"","period":"","bullets":[{"text":""}]}],
  "academics": [{"year":"","institute":"","course":"","percentage":""}],
  "skills": [{"name":"","level":""}],
  "certifications": [{"text":""}],
  "awards": [{"text":""}],
  "activities": [{"text":""}],
  "languages": [{"name":"","level":""}],
  "projects": [{"name":"","role":"","period":"","link":"","bullets":[{"text":""}]}]
}`;

// The backend's callAI endpoint is a generic (systemPrompt, userContent) → AI
// text proxy — it has no special "cv_chat" handling, so the multi-turn
// `messages` history has to be flattened into userContent here rather than
// sent as a separate field the server would otherwise silently ignore.
export async function callAIConversation({ messages, finalize = false, providerOverride = null }) {
  const transcript = (messages || [])
    .map(m => `${m.role === "user" ? "User" : "Assistant"}: ${m.content}`)
    .join("\n\n");

  if (finalize) {
    return apiPost("callAI", {
      operation: "cv_chat",
      systemPrompt: CV_CHAT_FINALIZE_SYSTEM,
      userContent: `Conversation transcript:\n\n${transcript}\n\nExtract a complete CV JSON object from everything the user shared above.`,
      json: true,
      providerOverride,
    });
  }

  return apiPost("callAI", {
    operation: "cv_chat",
    systemPrompt: CV_CHAT_SYSTEM,
    userContent: `Conversation so far:\n\n${transcript}\n\nRespond as the assistant's next message.`,
    providerOverride,
  });
}

// ── PDF export — returns a binary Blob, not JSON ──────────────────────────────
// Tries Level 1a (direct Cloud Function URL) then Level 1b (same function,
// same-origin via Hosting rewrite) before throwing so useCvExport.js can fall
// through to Level 2 (print-iframe) / Level 3 (html2canvas) on the client.
export async function exportPdfBinary({ html, filename = "CV.pdf", fitPage = false, pageSize = "a4" }) {
  const token = await getIdToken();
  const body  = { html, filename, fitPage, pageSize };

  let res;
  let usedFallback = false;

  // ── Level 1a: direct Cloud Function URL ────────────────────────────────────
  try {
    res = await _fetchPdf(PDF_PRIMARY_URL, body, PRIMARY_TIMEOUT_MS, token);
    if (res.status >= 500) {
      console.warn(`[exportPdf] Primary returned ${res.status} — retrying same-origin`);
      res = null;
    }
  } catch (e) {
    console.warn(`[exportPdf] Primary ${e.name === "AbortError" ? "timed out" : "network error"} — retrying same-origin`);
    res = null;
  }

  // ── Level 1b: same-origin retry (see comment above exportPdfBinary) ────────
  if (!res) {
    usedFallback = true;
    try {
      res = await _fetchPdf(PDF_FALLBACK_URL, body, FALLBACK_TIMEOUT_MS, token);
    } catch (e) {
      throw new Error(
        e.name === "AbortError"
          ? "Server PDF generation timeout (>60s) — falling back to print"
          : `Network error: ${e.message}`
      );
    }
  }

  // ── Parse response ─────────────────────────────────────────────────────────
  if (!res.ok) {
    let msg;
    try {
      const data = await res.json();
      msg = data.error || `Server error ${res.status}`;
    } catch {
      msg = `Server error ${res.status} (${res.statusText})`;
    }
    throw new Error(`${usedFallback ? "[fallback] " : ""}${msg}`);
  }

  const blob = await res.blob();
  if (!blob || blob.size === 0) {
    throw new Error("Server returned empty PDF");
  }

  const bytes = new Uint8Array(await blob.arrayBuffer());
  const isPdf =
    bytes.length >= 5 &&
    bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46; // %PDF
  if (!isPdf) {
    let hint = "";
    try { hint = new TextDecoder().decode(bytes.slice(0, 200)).trim(); } catch (_) {}
    throw new Error(
      hint.startsWith("{") || hint.startsWith("<")
        ? `Server returned an error instead of a PDF: ${hint.slice(0, 120)}`
        : "Server returned a corrupted PDF file"
    );
  }

  if (usedFallback) {
    // Surface in monitoring — helps track when the direct Cloud Functions URL
    // is flaky and the same-origin retry is doing the real work.
    console.info("[exportPdf] Served via same-origin retry (Level 1b)");
  }

  return new Blob([bytes], { type: "application/pdf" });
}

// ── Pre-warm the PDF Cloud Function ────────────────────────────────────────────
// Cold start (a fresh Chromium launch) is the dominant chunk of exportPdf's
// 10-25s worst case. This turns that wait into background time by launching
// (and immediately closing) a browser on the server the moment export intent
// is likely — e.g. hovering the Export button — well before the user actually
// clicks. Fire-and-forget: no return value, swallow all errors, never affects
// the real export path if it fails or is still in flight. Authenticated (not
// a public unauthenticated endpoint) so it can't be spammed by a stranger to
// exhaust the 10-instance cap; a signed-in user warming their own next export
// is the same trust boundary as the export endpoint itself already relies on.
export async function prewarmExportPdf() {
  try {
    const token = await getIdToken();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15_000);
    try {
      await fetch(PDF_PRIMARY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ warm: true }),
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timer);
    }
  } catch (_) {
    // Opportunistic only — a failed pre-warm just means the real export pays
    // the normal cold-start cost, exactly like before this existed.
  }
}
